import { z } from 'zod';
import { callReasonSchema } from './call-reason';
import { LLM_PROVIDERS } from './llm-providers';
import { REALTIME_PROVIDERS } from './realtime-providers';
import { stepPlanSchema } from './step-plan';
import { e164Schema } from './phone';

/**
 * Caller-audio enhancement modes: `bvc` (Krisp telephony background-voice
 * cancellation), `nc` (noise only), `none`. The one list every schema and the
 * voice worker's `VOICE_INPUT_FILTER` validate against.
 */
export const INPUT_FILTER_MODES = ['bvc', 'nc', 'none'] as const;
export type InputFilterMode = (typeof INPUT_FILTER_MODES)[number];
export const inputFilterModeSchema = z.enum(INPUT_FILTER_MODES);

// Fallback provider configuration
export const voiceFallbackSchema = z.object({
  provider: z.enum(['elevenlabs', 'playht', 'cartesia', 'deepgram', 'openai']),
  voiceId: z.string(),
  model: z.string().optional(),
});

export const transcriberFallbackSchema = z.object({
  provider: z.enum(['deepgram', 'openai']),
  model: z.string().optional(),
});

export const llmFallbackSchema = z.object({
  provider: LLM_PROVIDERS,
  model: z.string(),
  baseUrl: z.string().optional(),
});

// Voice/TTS settings
export const voiceSettingsSchema = z.object({
  provider: z.enum(['elevenlabs', 'playht', 'cartesia', 'deepgram', 'openai']),
  voiceId: z.string(),
  speed: z.number().min(0.5).max(2.0).default(1.0),
  stability: z.number().min(0).max(1).optional(),
  similarityBoost: z.number().min(0).max(1).optional(),
  model: z.string().optional(), // e.g. 'eleven_turbo_v2_5' for ElevenLabs
  fallbacks: z.array(voiceFallbackSchema).optional(), // Fallback TTS providers in order
});

// Transcriber/STT settings
export const transcriberSettingsSchema = z.object({
  provider: z.enum(['deepgram', 'openai']),
  model: z.string().optional(),
  language: z.string().default('en'),
  fallbacks: z.array(transcriberFallbackSchema).optional(), // Fallback STT providers in order
});

// LLM settings
export const llmSettingsSchema = z.object({
  provider: LLM_PROVIDERS,
  model: z.string(),
  temperature: z.number().min(0).max(2).default(0.7),
  // Optional because multi-agent voice flows pass instructions via
  // voice.Agent({ instructions }) per-agent and leave this field unset.
  // When present, reject empty strings — a blank non-null system prompt is
  // always a misconfiguration (silently produces "generic assistant"
  // behavior) and should fail fast at the config boundary.
  systemPrompt: z.string().min(1).optional(),
  baseUrl: z.string().optional(), // For OpenAI-compatible endpoints (Qwen via Together/Groq, etc.)
  fallbacks: z.array(llmFallbackSchema).optional(), // Fallback LLM providers in order
});

// Call behavior settings
export const callSettingsSchema = z.object({
  firstMessage: z.string().optional(),
  maxDurationSeconds: z.number().default(600),
  silenceTimeoutSeconds: z.number().default(30),
  recordingEnabled: z.boolean().default(false),
  waitForUserFirst: z.boolean().default(false), // Wait for user to speak before agent's first message
});

// LLM settings for extraction (no systemPrompt needed)
export const extractionLLMSettingsSchema = z.object({
  provider: z.enum(['openai', 'anthropic', 'google']),
  model: z.string(),
  temperature: z.number().min(0).max(2).default(0),
});

// Structured output field definition (for post-call extraction)
export const structuredOutputFieldSchema = z.object({
  name: z.string(),
  type: z.enum(['string', 'boolean', 'number', 'array']),
  description: z.string(),
  // For enum-like strings, can specify allowed values
  enum: z.array(z.string()).optional(),
  // For arrays, specify the item type (defaults to string)
  itemType: z.enum(['string', 'boolean', 'number']).optional(),
});

export type ExtractionLLMSettings = z.infer<typeof extractionLLMSettingsSchema>;
export type StructuredOutputFieldDef = z.infer<typeof structuredOutputFieldSchema>;

// Tool definition
export const toolSchema = z.object({
  name: z.string(),
  description: z.string(),
  parameters: z.record(z.string(), z.unknown()).optional(),
});

// Type exports (defined before schema for recursive type)
export type VoiceFallback = z.infer<typeof voiceFallbackSchema>;
export type TranscriberFallback = z.infer<typeof transcriberFallbackSchema>;
export type LLMFallback = z.infer<typeof llmFallbackSchema>;
export type VoiceSettings = z.infer<typeof voiceSettingsSchema>;
export type TranscriberSettings = z.infer<typeof transcriberSettingsSchema>;
export type LLMSettings = z.infer<typeof llmSettingsSchema>;
export type CallSettings = z.infer<typeof callSettingsSchema>;
export type Tool = z.infer<typeof toolSchema>;

export type AgentConfig = {
  name: string;
  voice: VoiceSettings;
  transcriber: TranscriberSettings;
  llm: LLMSettings;
  call?: CallSettings;
  tools?: Tool[];
  templateVariables?: Record<string, string>;
  structuredOutputs?: StructuredOutputFieldDef[];
  /** LLM to use for post-call structured output extraction. Defaults to OpenAI gpt-4o-mini. */
  extractionLLM?: ExtractionLLMSettings;
  handoffs?: Array<{
    name: string;
    description: string;
    agentConfig: AgentConfig;
  }>;
};

// Main agent config (recursive for handoffs)
export const agentConfigSchema: z.ZodType<AgentConfig> = z.object({
  name: z.string(),
  voice: voiceSettingsSchema,
  transcriber: transcriberSettingsSchema,
  llm: llmSettingsSchema,
  call: callSettingsSchema.optional(),
  tools: z.array(toolSchema).optional(),
  templateVariables: z.record(z.string(), z.string()).optional(),
  structuredOutputs: z.array(structuredOutputFieldSchema).optional(),
  extractionLLM: extractionLLMSettingsSchema.optional(),
  handoffs: z
    .array(
      z.object({
        name: z.string(),
        description: z.string(),
        agentConfig: z.lazy(() => agentConfigSchema),
      })
    )
    .optional(),
});

// Outbound call request (sent from API to LiveKit service).
//
// `callReason` is required: the outbound handler routes through
// `runAppointmentSession` (the shared core), which composes the primary
// prompt from the call-reason registry. `agentConfig.systemPrompt` is
// accepted for backward compat but is no longer consumed at runtime.
/**
 * A dialled call's machine behaviour, flattened from a campaign step's
 * `onMachine` (whose `leave_message` also names a message slot the worker does
 * not need).
 */
export const outboundOnMachineSchema = z.enum(['hang_up', 'leave_message']);
export type OutboundOnMachine = z.infer<typeof outboundOnMachineSchema>;

export const outboundCallRequestSchema = z.object({
  agentConfig: agentConfigSchema,
  fromPhone: e164Schema,
  toPhone: e164Schema,
  callReason: callReasonSchema,
  // First-class realtime-stack override (honored only on the realtime
  // path). Typed here rather than smuggled through `metadata` so a rename
  // is a compile error on both producer and consumer instead of silently
  // dropping the override.
  realtimeProvider: REALTIME_PROVIDERS.optional(),
  realtimeModel: z.string().min(1).optional(),
  /**
   * WHO speaks on this call, and WHAT TECHNOLOGY carries it (§5).
   *
   * Ids into the shared catalog (`voice/catalog.ts`), never provider values — a
   * persona names a CATALOG voice precisely so the same persona can move
   * between stacks without changing who the patient hears.
   *
   * Threaded exactly like `realtimeModel` above: the producer puts them in the
   * request's `metadata` record and the dispatcher reads them as typed fields.
   * Absent means the deployment default, which is what every call sent before
   * these existed.
   */
  personaId: z.string().min(1).optional(),
  agentId: z.string().min(1).optional(),
  /**
   * Caller-audio enhancement for THIS call: `bvc` (Krisp telephony
   * background-voice cancellation, the deployment default), `nc` (noise
   * only) or `none`. Per-call so a two-call experiment leaves every other
   * patient on the default; absent means the worker's `VOICE_INPUT_FILTER`.
   */
  inputFilter: inputFilterModeSchema.optional(),
  /**
   * The workflow version a campaign step pinned (ADR-318, P1/P2). When set, the
   * worker runs exactly this version from the database, whatever
   * `PUBLISHED_WORKFLOWS_ENABLED` says; absent, the call is routed by purpose as
   * before. Only campaign dials send it.
   */
  workflowVersionId: z.string().uuid().optional(),
  /**
   * What to do when the far end is a machine: hang up, or leave the callback
   * message. Absent means `leave_message`, which is what every call did before
   * a campaign could choose (ADR-318).
   */
  onMachine: outboundOnMachineSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type OutboundCallRequest = z.infer<typeof outboundCallRequestSchema>;

/**
 * The chart an outbound dial is about, as it travels in `metadata` (ADR-333).
 *
 * An outbound call verifies whoever answers against ONE chart's date of birth,
 * and nothing else opens the gate. A dial without the chart, or without a date
 * of birth on it, could only end in "I'm not able to confirm your details" after
 * the patient has picked up, so it is refused before anything rings.
 *
 * SHARED, so the producer and the worker cannot drift on the keys: the API
 * builds this object by type, and the worker checks it with this schema.
 */
export const outboundDialChartSchema = z.object({
  patientId: z.string().uuid(),
  patientDateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type OutboundDialChart = z.infer<typeof outboundDialChartSchema>;

/**
 * Why a dial was refused before it was placed. The worker returns one of these
 * as the dial error and the API classifies on the same constant — matching on
 * wording would file our own refusal as the patient not answering the day
 * someone reworded it.
 */
export const OUTBOUND_DIAL_REFUSAL = {
  NO_CHART: 'Outbound call refused: no patient chart on the request',
  NO_DOB: 'Outbound call refused: the patient has no date of birth on file',
} as const;
export type OutboundDialRefusal =
  (typeof OUTBOUND_DIAL_REFUSAL)[keyof typeof OUTBOUND_DIAL_REFUSAL];

/** Why this dial's metadata must not ring anyone, or null when it may. Names the field, never a value. */
export function outboundDialRefusal(metadata: unknown): OutboundDialRefusal | null {
  const parsed = outboundDialChartSchema.safeParse(metadata ?? {});
  if (parsed.success) return null;
  return parsed.error.issues.some((issue) => issue.path[0] === 'patientId')
    ? OUTBOUND_DIAL_REFUSAL.NO_CHART
    : OUTBOUND_DIAL_REFUSAL.NO_DOB;
}

// Outbound call metadata (passed in SIP participant metadata, received by voice agent).
//
// The `transport` field is authoritative for dispatcher routing — readers should
// branch on it, not on metadata-shape detection. Required going forward; payloads
// from older callers that lack it are rejected by Zod.
export const outboundCallMetadataSchema = z.object({
  transport: z.literal('sip-outbound'),
  agentConfig: agentConfigSchema,
  fromPhone: z.string().min(1),
  toPhone: z.string().min(1),
  interactionId: z.string().uuid().optional(),
  patientId: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  /**
   * Driving task ID — the task the API producer created before dialing.
   * Threaded through to the recording-egress producer so the GCS path
   * can be scoped per-task (`livekit/audio-recording/{org}/{task}/...`).
   * Absent on legacy dial paths or sandbox dispatches that don't have a
   * driving task; those skip recording.
   */
  taskId: z.string().uuid().optional(),
  /**
   * Patient DOB (YYYY-MM-DD). Drives the age-adaptive voice profile
   * (TTS speed, turn-detection endpointing, elderly prompt inclusion).
   * Optional — missing DOB falls through to the default (adult) profile.
   *
   * Producers should populate this from the patient record rather than
   * via the untyped `agentConfig.templateVariables.patientDob` key,
   * which the handler still reads as a legacy fallback.
   */
  patientDateOfBirth: z.string().optional(),
  /**
   * On-file chart data, each pre-formatted to a single confirmable line,
   * for the voice agent's `confirm_details` step (read back + confirm/
   * correct). `patientAddress` e.g. "123 Main St, Springfield, IL 62704";
   * `patientInsurance` is the primary carrier / payer name. Optional —
   * absent fields are skipped (the agent collects a phone fresh if even
   * that is missing). Producers format these from the patient record at
   * dispatch via `formatChartFieldsForCall`.
   */
  patientAddress: z.string().optional(),
  patientInsurance: z.string().optional(),
  /**
   * Why the call exists. Drives the primary agent's opener + preloaded context
   * chunk. Added for the outbound-handler migration onto `runAppointmentSession`
   * (see ADR-039). Required — producers must derive the reason from interaction
   * metadata at dispatch time.
   */
  callReason: callReasonSchema,
  /**
   * Per-call override for the realtime (speech-in/speech-out) stack,
   * mirroring the browser-test metadata fields. Honored only when the
   * LiveKit session runs the realtime path; the pipelined path ignores
   * them. Optional — absent falls through to the worker's env default.
   * Threaded through the `metadata` record on `OutboundCallRequest` and
   * validated here (producer puts them in `metadata`, dispatcher reads
   * them as typed fields) — the same pattern as `interactionId`/`taskId`.
   */
  realtimeProvider: REALTIME_PROVIDERS.optional(),
  realtimeModel: z.string().min(1).optional(),
  /**
   * WHO speaks on this call, and WHAT TECHNOLOGY carries it (§5).
   *
   * Ids into the shared catalog (`voice/catalog.ts`), never provider values — a
   * persona names a CATALOG voice precisely so the same persona can move
   * between stacks without changing who the patient hears.
   *
   * Threaded exactly like `realtimeModel` above: the producer puts them in the
   * request's `metadata` record and the dispatcher reads them as typed fields.
   * Absent means the deployment default, which is what every call sent before
   * these existed.
   */
  personaId: z.string().min(1).optional(),
  agentId: z.string().min(1).optional(),
  /** Caller-audio enhancement for this call; see `outboundCallRequestSchema`. */
  inputFilter: inputFilterModeSchema.optional(),
  /**
   * A campaign step's pinned workflow version; see `outboundCallRequestSchema`.
   * MUST be declared here as well: this is a plain `z.object`, which strips
   * unknown keys, so a field missing here never reaches the worker.
   */
  workflowVersionId: z.string().uuid().optional(),
  /** Machine behaviour for this call; see `outboundCallRequestSchema`. */
  onMachine: outboundOnMachineSchema.optional(),
});

export type OutboundCallMetadata = z.infer<typeof outboundCallMetadataSchema>;

// Browser-test metadata — passed in the LiveKit room metadata when a browser test
// call (task drawer, workflow builder) spins up a session. The dispatcher checks
// for `mode === 'browser-test'` and routes to the browser-test handler instead of
// the SIP inbound path.
//
// Self-contained: the token endpoint denormalizes org/location/patient names into
// the metadata so the agent worker doesn't need additional internal fetches just
// to warm up prompts.
//
// See docs/conversation-view.md.
/**
 * A builder test's inputs, as the worker needs them (ADR-306).
 *
 *   scope           the call's scheduling scope, the same four fields a
 *                   referral's call reason carries, so the worker builds its
 *                   `schedulingConstraints` the same way.
 *   appointment     the made-up appointment the test patient "has", shaped as
 *                   the scheduling port returns one.
 *   visitTypeNames  names for the report, by appointment-type id.
 */
export const builderTestInputsSchema = z
  .object({
    scope: z
      .object({
        providerId: z.string().uuid().optional(),
        locationId: z.string().uuid().optional(),
        appointmentTypeId: z.string().uuid().optional(),
        durationMinutes: z.number().int().positive().optional(),
      })
      .strict()
      .optional(),
    appointment: z
      .object({
        id: z.string().uuid(),
        startTime: z.string().datetime({ offset: true }),
        providerName: z.string().nullable(),
        locationName: z.string().nullable(),
        timezone: z.string().nullable(),
        reasonForVisit: z.string().nullable(),
        cancellationFee: z
          .object({ amountCents: z.number().int(), noticeHours: z.number() })
          .strict()
          .nullable(),
      })
      .strict()
      .optional(),
    visitTypeNames: z.record(z.string(), z.string()).optional(),
  })
  .strict();
export type BuilderTestInputs = z.infer<typeof builderTestInputsSchema>;

export const browserTestMetadataSchema = z.object({
  /** Authoritative transport id — dispatcher routes on this, not on shape. */
  transport: z.literal('browser-test'),
  /** @deprecated Retained for payloads from pre-transport-field callers; dispatcher now reads `transport`. */
  mode: z.literal('browser-test').optional(),

  organizationId: z.string().uuid(),
  organizationName: z.string(),
  locationId: z.string().uuid().optional(),
  locationName: z.string().optional(),

  patientId: z.string().uuid(),
  patientName: z.string(),
  patientDob: z.string().optional(),
  patientPhone: z.string().optional(),
  // NO DIALLED LINE FIELD, AND ITS ABSENCE IS THE DESIGN (ADR-197).
  //
  // A browser test is not a phone call, so it has no line — and every
  // attempt to give it one was a number somebody invented. ADR-185/190 put
  // the CHART number here so `OfferSms` had a destination to name, and the
  // harness then read back the patient's last four and texted them for real.
  // ADR-195 replaced it with an operator-supplied test number, which worked
  // only for as long as a required form field kept being filled in correctly.
  //
  // With the field gone, `OfferSms` resolves no destination from anything the
  // framework holds and asks the person on the call for one — which is both
  // the honest behaviour for a line we do not know and a containment property
  // that cannot be forgotten. This schema strips unknown keys, so a room
  // minted by an API that still sends `toPhone` loses it here rather than
  // reintroducing a line.
  /**
   * On-file chart data pre-formatted to confirmable lines for the
   * `confirm_details` step (see the outbound metadata schema above for
   * the full rationale). Optional — absent fields are skipped.
   */
  patientAddress: z.string().optional(),
  patientInsurance: z.string().optional(),

  /**
   * Who initiated the call. The primary agent uses this to pick the opener
   * and decide when to verify identity. Defaults to 'inbound' (patient
   * called us) to stay backwards compatible with existing test runs.
   */
  direction: z.enum(['inbound', 'outbound']).default('inbound'),

  /**
   * Why the call exists. Drives the opener template and what preloaded
   * context the primary agent sees. Defaults to `schedule_referral` when
   * referralId + referralContext are present, otherwise `general_inquiry`
   * (enforced server-side by the token endpoint).
   *
   * Shape comes from `./call-reason.ts` — `{ intent, context }`. Do not
   * inline variants here.
   */
  callReason: callReasonSchema.default({ intent: 'general_inquiry', context: {} }),

  /**
   * @deprecated Use `callReason.intent === 'schedule_referral'` and read
   * `callReason.context.referralId` instead. Kept for compatibility with
   * older token payloads until the API fully migrates.
   */
  referralId: z.string().uuid().optional(),

  /** @deprecated Use `callReason.context.referralContext`. */
  referralContext: z.string().optional(),

  /** Pre-created interaction to link the tracker to. */
  interactionId: z.string().uuid(),

  /**
   * Driving task the call was launched from. Optional — `general_inquiry`
   * tests can be started without a task, and `schedule_referral` can be
   * launched from a documentId alone. When present, the recording egress
   * uses this to scope the GCS object path; when absent, the path uses
   * the `no-task` sentinel.
   */
  taskId: z.string().uuid().optional(),

  /**
   * Still sent by producers, read by nothing: the workflow's identity gate runs
   * on every call that reaches it (ADR-333). Kept so older payloads parse.
   */
  preVerified: z.boolean().default(true),

  /** Display name for the caller (defaults to patient name). */
  callerName: z.string().optional(),

  /**
   * Per-call override for the realtime stack provider. Honored only
   * when the LiveKit session runs the realtime path. When set,
   * overrides the env default (`V2_REALTIME_PROVIDER`) for this
   * single session.
   */
  realtimeProvider: REALTIME_PROVIDERS.optional(),

  /**
   * Per-call override for the realtime model id within the chosen
   * provider. Free-form string. Undefined → builder falls back to
   * `REALTIME_DEFAULTS[provider].model`. Honored only on realtime.
   */
  realtimeModel: z.string().min(1).optional(),
  /** Caller-audio filter for this call (EXP-0024 W2) — see `outboundCallRequestSchema`. */
  inputFilter: inputFilterModeSchema.optional(),
  /**
   * WHO speaks on this call, and WHAT TECHNOLOGY carries it (§5).
   *
   * Ids into the shared catalog (`voice/catalog.ts`), never provider values — a
   * persona names a CATALOG voice precisely so the same persona can move
   * between stacks without changing who the patient hears.
   *
   * Threaded exactly like `realtimeModel` above: the producer puts them in the
   * request's `metadata` record and the dispatcher reads them as typed fields.
   * Absent means the deployment default, which is what every call sent before
   * these existed.
   */
  personaId: z.string().min(1).optional(),
  agentId: z.string().min(1).optional(),
  /**
   * Run only SOME of the workflow — the steps a builder step test stands in
   * ahead of the one under test (ADR-306). Only a builder test call carries
   * one; the API composes it from the draft, never the caller.
   *
   * Declared here explicitly because this object STRIPS unknown keys — a
   * field the API sent and this schema did not name would vanish at the
   * dispatcher, and the tester would hear the whole conversation while
   * believing they had asked for one step.
   */
  testPlan: stepPlanSchema.optional(),
  /**
   * A builder draft under test (ADR-275): the snapshot this call runs instead
   * of any code-defined or published workflow. A call carrying one that
   * cannot be loaded ends rather than running something else.
   */
  workflowSnapshotId: z.string().uuid().optional(),
  /**
   * What the tester picked for a builder test (ADR-306), resolved and checked
   * inside the caller's organization by the API. Read only when
   * `workflowSnapshotId` is set.
   */
  builderTestInputs: builderTestInputsSchema.optional(),
});

export type BrowserTestMetadata = z.infer<typeof browserTestMetadataSchema>;
